import { useCallback, useEffect, useRef, useState } from "react";

import { searchCvhVehiclesAPI } from "services/api/catalog/cvh/api";
import { CvhVehicleSearchRow } from "services/api/catalog/cvh/types";
import { initialPagination } from "ui/components/Table/consts";

const PAGE_SIZE = 20;

/**
 * Paginirani dohvat CVH vozila po JOM broju s infinite-scrollom (lazyload).
 * Prva stranica se (ponovno) dohvaća kad se promijeni pretraga; `loadMore` dodaje iduće stranice.
 */
export const usePaginatedJoms = (search: string) => {
  const [joms, setJoms] = useState<CvhVehicleSearchRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Sprječava out-of-order rezultate kad se pretraga promijeni usred dohvata.
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(
    async (pageNumber: number) => {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      try {
        const res = await searchCvhVehiclesAPI(
          { ...initialPagination, pageNumber, pageSize: PAGE_SIZE },
          search
        );

        if (requestId !== requestIdRef.current) return;

        setJoms((prev) => (pageNumber === 1 ? res.data : [...prev, ...res.data]));
        setTotalPages(
          res.pagination.totalPages ??
            Math.max(1, Math.ceil((res.pagination.totalCount ?? res.data.length) / PAGE_SIZE))
        );
        setPage(pageNumber);
      } finally {
        if (requestId === requestIdRef.current) setIsLoading(false);
      }
    },
    [search]
  );

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (isLoading || page >= totalPages) return;
    fetchPage(page + 1);
  }, [isLoading, page, totalPages, fetchPage]);

  const refresh = useCallback(() => fetchPage(1), [fetchPage]);

  return { joms, isLoading, hasMore: page < totalPages, loadMore, refresh };
};
